/// <amd-module name="Build/ArtifactsView/Components/PublishedArtifactsView" />
import * as React from "react";

import { BuildArtifactTypes } from "Build/Common/Artifacts/Common";
import { ArtifactsTreeData, ArtifactsTreeItem } from "Build/Client/Contracts/DataProvider";
import { IBuildLinkingService } from "Build/Client/Linking";
import { openLinkSecurely } from "Build/Common/Library/Navigation";
import { PermissionsStore } from "Build/Common/Library/Stores/Permissions";
import { Security } from "Build/Common/Library/Security";
import { TreeColumnAction, TreeColumnActionProps } from "Build/ArtifactsView/Components/TreeColumnAction";
import * as Resources from "Build/ArtifactsView/Resources";
import { IPublishedArtifactsService } from "Build/ArtifactsView/PublishedArtifactsService.types";
import { ArtifactsHeaderBreadcrumbService } from "Build/ArtifactsView/Components/ArtifactsHeaderBreadcrumbService";
import { IVssHistoryService } from "VSS/Platform/History";
import { RouteParameters } from "Build/Client/Linking";
import { NavConstants } from "Build/Common/Library/Constants";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import * as StringUtil from "VSS/Core/Util/String";
import {
    IVssComponentContext,
    IVssComponentProperties,
    VssComponent
} from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import {Telemetry} from "Build/ArtifactsView/Constants"
import { Card } from "VSSUI/Card";
import { ISimpleListCell } from "VSSUI/List";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { ISimpleTableCell, TableColumnLayout } from "VSSUI/Table";
import { ITreeColumn, renderExpandableTreeCell, renderTreeCell, Tree, ITreeRow } from "VSSUI/TreeEx";
import { ITreeItem, ITreeItemEx, ITreeItemProvider, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";

type ArtifactTreeData = IArtifactTableItem | IReadonlyObservableValue<IArtifactTableItem | undefined>;

const ArtifactDirectory = "directory";
const ArtifactFile = "file";

interface IArtifactTableItem extends ISimpleTableCell {
    name: ISimpleListCell;
    size: string;
    artifactId: number;
    fullPath: string;
    type: string;
}

interface IPublishedArtifactsTabProps extends IVssComponentProperties {
    service: IPublishedArtifactsService;
}

export class PublishedArtifactsView extends VssComponent<IPublishedArtifactsTabProps> {

    private readonly _nameColumn: ITreeColumn<IArtifactTableItem>;
    private readonly _columns: ITreeColumn<IArtifactTableItem>[];
    private readonly _data: IReadonlyObservableValue<ArtifactsTreeData | undefined>;
    private readonly _itemProvider: TreeItemProvider<ArtifactTreeData>;
    private readonly _isUserMember: boolean;
    private _displayedArtifactIds: Set<number>;

    constructor(props: IPublishedArtifactsTabProps, context: IVssComponentContext) {
        super(props, context);

        const pageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const pageData = pageService.getData();
        const projectId = pageData && pageData.project && pageData.project.id;

        const nameColumnWidth = -58;
        const sizeColumnWidth = -28;

        if (projectId) {
            const permissionsStore = new PermissionsStore({
                pageContext: this.context.pageContext
            });
            const security = new Security({
                store: permissionsStore,
                projectId: projectId
            });
            this._isUserMember = security.isMember();
        }
        else {
            this._isUserMember = false;
        }

        this._data = this.props.service.data;
        this._itemProvider = this._getItemProvider();
        this._displayedArtifactIds = this._getDisplayedArtifactIds();

        const hasExplorableArtifacts = this._data.value && this._data.value.items.some(item => this._canExplore(item.type));
        this._nameColumn = {
            hierarchical: hasExplorableArtifacts,
            id: "name",
            name: Resources.ArtifactsColumnName,
            renderCell: hasExplorableArtifacts ? renderExpandableTreeCell : renderTreeCell,
            width: nameColumnWidth,
            columnLayout: TableColumnLayout.singleLinePrefix
        };
        this._columns = [this._nameColumn];

        if (hasExplorableArtifacts) {
            const sizeColumn: ITreeColumn<IArtifactTableItem> = {
                id: "size",
                name: Resources.ArtifactsColumnSize,
                renderCell: renderTreeCell,
                width: sizeColumnWidth
            };
            this._columns.push(sizeColumn);
        }

        const hasActionableArtifacts = this._data.value && this._data.value.items.some(item => this._isDownloadableItem(item.type) || this._isCopyableItem(item.type));
        if (hasActionableArtifacts) {
            const props: TreeColumnActionProps<IArtifactTableItem> = {
                pageContext: this.context.pageContext,
                copyAvailable: this._isCopyAvailable,
                getCopyUrl: this._getCopyUrl,
                downloadAvailable: this._isDownloadAvailable,
                downloadItem: this._downloadItem,
                getDownloadUrl: this._getDownloadUrl
            };
            const actionColumn = new TreeColumnAction<IArtifactTableItem>(props);
            this._columns.push(actionColumn);
        }
    }

    public render() {
        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="run-artifacts-view">
                    <Observer data={this._data}>
                        {(observerProps: { data: ArtifactsTreeData | undefined }) => {
                            return <div className="run-artifacts-content page-content page-content-top">
                                {
                                    observerProps.data && observerProps.data.items && observerProps.data.items.length > 0
                                        ? <Card
                                            className="flex-grow bolt-table-card"
                                            contentProps={{ contentPadding: false }}>
                                            {
                                                this._renderArtifactsTree()
                                            }
                                        </Card>
                                        : <Card className="flex-grow">{this._renderNoArtifacts()}</Card>
                                }
                            </div>
                        }}
                    </Observer>
                </Page>
            </Surface>
        );
    }

    public componentDidMount() {
        if (this._data.value) {
            this._setBreadcrumbs(this._data.value);
            document.title = StringUtil.format(Resources.TitleArtifacts, this._data.value.buildNumber);
        }

        // We should have everything on first render
        this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService").markTimeToInteractive(Telemetry.Area, Telemetry.RunArtifacts);

        this._data.subscribe(this._onDataUpdated);
        this.props.service.startListening();
    }

    public componentWillUnmount() {
        this._data.unsubscribe(this._onDataUpdated);
        this.props.service.stopListening();
    }

    private _onDataUpdated = (): void => {
        const rootItems = this._itemProvider.value.filter(item => !item.parentItem);
        const lastRootItem = rootItems.length > 0 ? (rootItems[rootItems.length - 1]).underlyingItem : undefined;
        const itemsToAdd = [
            {
                // Put the new artifact at the end
                insertAfter: lastRootItem,
                items: this._convertToTreeItems(this._data.value!.items.filter(item => !this._displayedArtifactIds.has(item.artifactId)))
            }
        ];

        // Add the new artifacts to the root
        this._itemProvider.splice(undefined, undefined, itemsToAdd);
        this._displayedArtifactIds = this._getDisplayedArtifactIds();

        const hasExplorableArtifacts = this._data.value && this._data.value.items.some(item => this._canExplore(item.type));
        this._nameColumn.hierarchical = hasExplorableArtifacts;
        this._nameColumn.renderCell = hasExplorableArtifacts ? renderExpandableTreeCell : renderTreeCell;
    }

    private _setBreadcrumbs = (data: ArtifactsTreeData): void => {
        const linkingService = this.context.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const pipelineName = data.definitionName;
        const pipelineUrl = linkingService.getDefinitionUrl(data.definitionId);
        const runNumber = data.buildNumber;
        const runUrl = linkingService.getBuildUrl(data.buildId);
        const artifactsView = Resources.PublishedArtifactsText;
        const artifactsViewUrl = linkingService.getBuildUrl(data.buildId, NavConstants.artifacts, undefined, undefined, { [RouteParameters.Type]: historyService.getState()[RouteParameters.Type]});
        const breadcrumbService = this.context.pageContext.getService<ArtifactsHeaderBreadcrumbService>("artifacts-header-breadcrumb-service");
        breadcrumbService.setBreadcrumbs(pipelineName, pipelineUrl, runNumber, runUrl, artifactsView, artifactsViewUrl);
    }

    private _renderNoArtifacts = (): JSX.Element => {
        return (
            <>
                {Resources.ArtifactsNonePublished}
            </>
        );
    }

    private _renderArtifactsTree = (): JSX.Element => {
        return (
            <Tree<ArtifactTreeData>
                columns={this._columns}
                itemProvider={this._itemProvider}
                onActivate={this._onActivate}
                onToggle={this._onToggle}
                ariaLabel={Resources.PublishedArtifactsText}
            />
        );
    }

    private _getDisplayedArtifactIds = (): Set<number> => {
        if (!this._data.value || !this._data.value.items) {
            return new Set<number>();
        }
        return new Set<number>(this._data.value.items.map(item => item.artifactId));
    }

    private _getItemProvider(): TreeItemProvider<ArtifactTreeData> {
        const items = ((this._data.value && this._data.value.items) || []).sort(this._artifactTreeItemComparer);
        return new TreeItemProvider<ArtifactTreeData>(this._convertToTreeItems(items));
    }

    private _artifactTreeItemComparer = (a: ArtifactsTreeItem, b: ArtifactsTreeItem): number => {
        return StringUtil.localeIgnoreCaseComparer(a.name, b.name);
    }

    private _createTreeItem = (
        tableItem: IArtifactTableItem,
        artifactType: string,
        childItems?: ITreeItem<ArtifactTreeData>[]): ITreeItem<ArtifactTreeData> => {
        let children = childItems;
        if (!children) {
            if (this._canExplore(artifactType)) {
                // For container/drop/pipeline artifacts, assume they will have contents
                children = [{ data: new ObservableValue(undefined) }];
            }
        }

        return {
            data: tableItem,
            expanded: false,
            childItems: children
        };
    }

    private _getIcon = (artifact: ArtifactsTreeItem): string => {
        if (StringUtil.equals(artifact.type, BuildArtifactTypes.Container, true) ||
            StringUtil.equals(artifact.type, BuildArtifactTypes.PipelineArtifact, true) ||
            StringUtil.equals(artifact.type, BuildArtifactTypes.FilePath, true)) {
            return "Package";
        }
        if (StringUtil.equals(artifact.type, BuildArtifactTypes.GitRef, true)) {
            return "OpenSource";
        }
        if (StringUtil.equals(artifact.type, BuildArtifactTypes.VersionControl, true) ||
            StringUtil.equals(artifact.type, BuildArtifactTypes.TfvcLabel, true)) {
            return "TFVCLogo";
        }
        if (StringUtil.equals(artifact.type, ArtifactFile, true)) {
            return "Page";
        }
        if (StringUtil.equals(artifact.type, ArtifactDirectory, true)) {
            return "FolderHorizontal";
        }
        return "ZipFolder";
    }

    private _createTableItem = (artifact: ArtifactsTreeItem): IArtifactTableItem => {
        return {
            name: {
                // Convert full paths to names
                text: this._getArtifactShortName(artifact),
                iconProps: { iconName: this._getIcon(artifact) }
            },
            size: this._getFileSizeText(artifact.size),
            artifactId: artifact.artifactId,
            fullPath: artifact.sourcePath,
            type: artifact.type
        } as IArtifactTableItem;
    }

    private _getArtifactShortName = (artifact: ArtifactsTreeItem): string => {
        // Show the full name for FilePath artifacts
        if (StringUtil.equals(artifact.type, BuildArtifactTypes.FilePath, true)) {
            return artifact.name;
        }
        return artifact.name.substring(artifact.name.lastIndexOf('/') + 1);
    }

    private _getFileSizeText(size: number): string {
        if (size <= 0) {
            return "";
        }
        if (size < 1024) {
            return `${size} B`;
        }
        if (size < 1048576) {
            return `${Math.ceil(size / 1024)} KB`;
        }
        return `${Math.ceil(size / 1048576)} MB`;
    }

    private _onToggle = async (event: React.SyntheticEvent<HTMLElement>, treeItemEx: ITreeItemEx<IArtifactTableItem>): Promise<void> => {
        const treeItem = treeItemEx.underlyingItem;
        this._itemProvider.toggle(treeItem);

        // The tree avoids re-fetching on its own
        if (this._canExplore(treeItem.data.type)) {
            const data = await this.props.service.getArtifactTree(treeItem.data.artifactId);
            if (data) {
                this._itemProvider.splice(treeItem, treeItem.childItems, [
                    {
                        items: this._convertToTreeItems(data.items)
                    }
                ]);
            }
            else {
                this._itemProvider.splice(treeItem, treeItem.childItems);
            }
        }
    }

    private _onActivate = (event: React.SyntheticEvent<HTMLElement>, treeRow: ITreeRow<IArtifactTableItem>): void => {
        const artifact = treeRow.data.underlyingItem.data;
        if (StringUtil.equals(artifact.type, ArtifactFile, true)) {
            this._downloadItem(artifact);
        } else {
            this._onToggle(event, treeRow.data);
        }
    }

    private _convertToTreeItems = (items: ArtifactsTreeItem[]): ITreeItem<ArtifactTreeData>[] => {
        return items.map(item => this._createTreeItem(this._createTableItem(item), item.type, item.items && this._convertToTreeItems(item.items)));
    }

    private _canExplore = (artifactType: string): boolean => {
        return this._isUserMember &&
            (StringUtil.equals(artifactType, BuildArtifactTypes.Container, true)
                || StringUtil.equals(artifactType, BuildArtifactTypes.PipelineArtifact, true));
    }

    private _isDownloadAvailable = (artifact: IArtifactTableItem): boolean => {
        return this._isDownloadableItem(artifact.type);
    }

    private _isDownloadableItem = (artifactType: string): boolean => {
        return StringUtil.equals(artifactType, BuildArtifactTypes.Container, true)
            || StringUtil.equals(artifactType, BuildArtifactTypes.PipelineArtifact, true)
            || StringUtil.equals(artifactType, ArtifactFile, true)
            || StringUtil.equals(artifactType, ArtifactDirectory, true);
    }

    private _downloadItem = async (artifact: IArtifactTableItem) => {
        const downloadUrl: string | undefined = await this._getDownloadUrl(artifact);
        if (downloadUrl) {
            openLinkSecurely(downloadUrl, false);
        }
    }
    private _getDownloadUrl = async (artifact: IArtifactTableItem): Promise<string | undefined> => {
        if (artifact) {
            let itemPath: string | undefined;
            if (StringUtil.equals(artifact.type, ArtifactFile, true) || StringUtil.equals(artifact.type, ArtifactDirectory, true)) {
                itemPath = artifact.fullPath;
            }

            const compressDownload = !StringUtil.equals(artifact.type, ArtifactFile, true);
            return await this.props.service.getDownloadUrl(artifact.artifactId, compressDownload, itemPath, artifact.type);
        }
        return undefined;
    }

    private _isCopyAvailable = (artifact: IArtifactTableItem): boolean => {
        return artifact && this._isCopyableItem(artifact.type);
    }

    private _isCopyableItem = (artifactType: string): boolean => {
        return StringUtil.equals(artifactType, BuildArtifactTypes.FilePath, true);
    }

    private _getCopyUrl = (artifact: IArtifactTableItem): string => {
        return this._isCopyAvailable(artifact) ? artifact.fullPath : "";
    }
}
